import type { DxfLayer }             from '@models/map/dxfTypes';
import type { DxfSuggestedCategory } from '@models/map/dxfTypes';
import type { PlanShape }            from '@models/map/dxfTypes';

/**
 * Choix des calques du plan à importer (étape « mapping » de {@link PlanImportSidebar}).
 *
 * Le serveur PROPOSE un découpage en catégories (`suggestedMapping` de `/plan/layers`, une par
 * calque de contours porteur de POI) mais ne l'impose pas : `POST /plan/mapping` accepte
 * n'importe quelles catégories `{key, label, baseLayer, numberLayer, briefLayer}` construites sur
 * les calques réellement présents. L'opérateur peut donc ici :
 *
 *  - décocher une catégorie proposée qu'il ne veut pas importer ;
 *  - cocher un calque que la détection n'a pas retenu (typiquement une zone sans étiquette, que la
 *    cascade classe « base » sans jamais la proposer, faute de calque de numéros apparié) ;
 *  - corriger le calque de numéros associé, l'appariement serveur étant best-effort.
 *
 * ⚠️ **Une catégorie sans calque de numéros produit désormais des POI**, muets. La priorité de
 * l'import est de créer les FORMES ; un numéro absent se saisit à la main dans l'éditeur, une
 * forme jamais importée ne se rattrape pas. L'avertissement affiché a donc été retourné : il
 * n'annonce plus « aucun POI », il dit que les POI seront sans numéro.
 *
 * Sur un plan raster (image, PDF scanné), le serveur propose **une ligne par forme** : plusieurs
 * catégories partagent le même `baseLayer` et ne se distinguent que par `shapes`.
 */

/** Une catégorie candidate à l'import, proposée par le serveur ou ajoutée par l'opérateur. */
export type PlanCategory = {
	key: string;
	label: string;
	/** Calque des contours : c'est LUI qui fournit les formes importées. */
	baseLayer: string;
	/** Calque des numéros : sans lui, les POI de cette catégorie sont créés SANS numéro. */
	numberLayer: string | null;
	briefLayer: string | null;
	/** Rangs des formes désignées dans `baseLayer`, ou null pour tout le calque. */
	shapes: number[] | null;
	/**
	 * Rangs à importer MALGRÉ le rejet de la liste blanche de formes simples — le miroir de
	 * `shapes`. Alimenté par la file des doutes, jamais à la main : c'est un opt-in forme par
	 * forme, pas un assouplissement de la règle.
	 */
	forcedShapes: number[] | null;
	/**
	 * Numéros SAISIS par l'opérateur dans la file des doutes, rang => texte.
	 *
	 * Ils écrasent ce que l'appariement géométrique a trouvé — l'opérateur regarde le plan,
	 * l'appariement regarde des coordonnées. C'est la sortie des doutes `glyph_unreadable`
	 * (numéro vectorisé illisible) et `no_number` (contour que nul libellé ne touche).
	 */
	numbers: Record<number, string>;
	enabled: boolean;
	/** URN du PlaceType choisi — `''` tant que l'opérateur n'en a pas choisi. */
	placeTypeUrn: string;
	/**
	 * **Champ sur lequel un POI déjà présent est reconnu au réimport** : `'label'`,
	 * `'reference'`, ou l'URN d'un `Field` du type choisi.
	 *
	 * `''` = laisser le serveur décider, qui retombe alors sur `'label'`. Or `label` porte le
	 * nom de l'exposant et non le numéro de stand : le défaut RECRÉE les POI en doublon à
	 * chaque réimport d'un hall, sans rien signaler. `POST /plan/match-preview` mesure ce que
	 * chaque candidat donnerait, et l'écran pré-sélectionne le mieux placé.
	 */
	matchField: string;
	/** Vrai si la catégorie vient de `suggestedMapping`, faux si elle vient d'un calque brut. */
	suggested: boolean;
	/** Reprise d'un mapping validé du projet ou du compte. */
	remembered?: boolean;
};

/**
 * Nom de pseudo-calque FABRIQUÉ par le serveur faute de structure exploitable dans le plan
 * (`fill rgb(3%, 36%, 58%) · stroke rgb(11%, 11%, 10%) · 0.225048`).
 *
 * Contrat miroir de `SvgStylePseudoLayerSource::isPseudoLayerName()` : volontairement laxiste
 * sur la valeur des couleurs, un `fill` pouvant être `#05adae`, `rgb(3%, …)` ou `none`.
 */
export function isPseudoLayerName(name: string): boolean {
	return (name.startsWith('fill ') && name.includes(' · stroke ')) || isRasterLayerName(name);
}

/**
 * Pseudo-calque fabriqué par le pipeline RASTER, nommé par sa couleur quantifiée
 * (`couleur #085a94`).
 *
 * Contrat miroir de `RasterColourLayerSource::isPseudoLayerName()`. Il était absent d'ici, et
 * ça se voyait : sur un import image ou un PDF scanné, aucune ligne n'avait de pastille et
 * toutes affichaient leur nom brut.
 */
export function isRasterLayerName(name: string): boolean {
	return /^couleur #[0-9a-f]{6}$/i.test(name);
}

/**
 * Noms AFFICHABLES des calques : « Forme 1 », « Forme 2 »… pour les pseudo-calques, le nom réel
 * pour les autres (un DXF ou un SVG structuré a de vrais noms de calques, bien plus parlants).
 *
 * Le nom brut reste la CLÉ partout ailleurs — il est persisté par `POST /plan/mapping` et doit
 * rester identique à ce que le serveur recalcule à l'extraction. On ne renomme donc que ce que
 * l'opérateur lit ; le compteur, lui, n'a de sens que dans l'ordre où le serveur liste les
 * calques, ce qui suffit puisque cet ordre est stable pour un import donné.
 */
export function buildLayerLabels(layers: DxfLayer[]): Map<string, string> {
	let shapeIndex = 0;

	return new Map(layers.map(layer => [
		layer.name,
		isPseudoLayerName(layer.name) ? `Forme ${++shapeIndex}` : layer.name,
	]));
}

/**
 * Couleur de pastille d'un pseudo-calque : son `fill`, ou son `stroke` si le remplissage est
 * absent (cas d'un calque de traits). C'est ce qui reste pour distinguer « Forme 1 » de
 * « Forme 2 » une fois le style retiré de l'étiquette.
 *
 * La valeur est reprise TELLE QUELLE du nom : `#05adae` comme `rgb(3.13%, 36.47%, 58.43%)`
 * sont des couleurs CSS valides.
 */
export function layerSwatch(name: string): string | null {
	if (isRasterLayerName(name)) return name.slice('couleur '.length);

	const match = /^fill (.+?) · stroke (.+?) · /.exec(name);
	if (!match) return null;

	return [match[1]!, match[2]!].find(colour => colour !== 'none') ?? null;
}

export const ROLE_LABELS: Record<DxfLayer['role'], string> = {
	base: 'contours',
	number: 'numéros',
	brief: 'équipement',
	other: 'autre',
};

/** Ordre d'affichage des calques non proposés : les plus plausibles d'abord. */
export const ROLE_ORDER: Record<DxfLayer['role'], number> = { base: 0, other: 1, brief: 2, number: 3 };

/**
 * Une ligne proposée donne-t-elle au moins une forme que l'import sait CRÉER ?
 *
 * `shapes` est facultatif partout : la liste des formes ne vient que de `/plan/analysis`, et
 * sans elle on ne peut rien conclure — on répond alors « oui », c'est-à-dire le comportement
 * d'avant.
 */
function hasImportableShape(category: DxfSuggestedCategory, importable: Set<string>, known: boolean): boolean {
	if (!known || !category.shapes?.length) return true;

	return category.shapes.some(rank => importable.has(shapeRef(category.baseLayer, rank)));
}

/** Désignation d'une forme, avec un séparateur qu'un nom de pseudo-calque ne peut pas porter. */
function shapeRef(layer: string, index: number): string {
	return `${layer} ${index}`;
}

/**
 * Catégories affichées : celles proposées par le serveur (cochées d'office), puis tout calque
 * qui n'est le contour d'aucune d'elles (décoché, à cocher pour l'ajouter).
 *
 * ## Une ligne dont AUCUNE forme n'est importable sort DÉCOCHÉE
 *
 * Sur un plan tramé, le serveur propose une ligne par forme, et certaines de ces formes ne sont
 * aucune des formes simples que l'import sait créer — un stand en L, typiquement. Les laisser
 * cochées était un mensonge : la case promettait « celle-ci sera importée » et rien n'en
 * sortait. La ligne RESTE — c'est par elle qu'on retrouve la forme, et la file des doutes
 * propose de la garder quand même — mais décochée.
 *
 * `shapes` est facultatif : sans lui (appel depuis `/plan/layers`, qui ne rend pas les formes),
 * tout est coché comme avant.
 */
export function buildCategories(
	layers: DxfLayer[],
	suggested: DxfSuggestedCategory[],
	shapes: PlanShape[] = [],
): PlanCategory[] {
	// `undefined` = la source ne dit rien du verdict (`/plan/shapes`, qui ne rend QUE de
	// l'importable) ; `null` = contour explicitement non reconnu. Les deux se distinguent.
	const importable = new Set(
		shapes.filter(shape => null !== shape.shape && undefined !== shape.shape)
			.map(shape => shapeRef(shape.layer, shape.index)),
	);
	const known = shapes.length > 0;

	const suggestedCategories: PlanCategory[] = suggested.map(category => ({
		...category,
		shapes: category.shapes ?? null,
		forcedShapes: category.forcedShapes ?? null,
		numbers: {},
		enabled: hasImportableShape(category, importable, known),
		placeTypeUrn: '',
		matchField: '',
		suggested: true,
	}));

	const usedAsBase = new Set(suggestedCategories.map(category => category.baseLayer));
	const usedKeys = new Set(suggestedCategories.map(category => category.key));
	// Un calque déjà découpé forme par forme ne doit pas revenir en entier : il produirait
	// une ligne de plus, qui recouvrirait toutes celles qu'on vient de proposer.
	const extraCategories: PlanCategory[] = layers
		.filter(layer => !usedAsBase.has(layer.name))
		.sort((a, b) => (ROLE_ORDER[a.role] - ROLE_ORDER[b.role]) || a.name.localeCompare(b.name))
		.map((layer, index) => ({
			key: uniqueKey(categoryKey(layer.name, index), usedKeys),
			label: layer.name,
			baseLayer: layer.name,
			numberLayer: null,
			briefLayer: null,
			shapes: null,
			forcedShapes: null,
			numbers: {},
			enabled: false,
			placeTypeUrn: '',
			matchField: '',
			suggested: false,
		}));

	return [...suggestedCategories, ...extraCategories];
}

/**
 * Clé de catégorie pour un calque désigné à la main — même normalisation que le serveur pour ses
 * propres suggestions (`LayerDetectionCascade::slug`), le nom de calque brut pouvant contenir
 * n'importe quoi (espaces, accents, ponctuation).
 */
function categoryKey(layerName: string, index: number): string {
	const slug = layerName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

	return slug || `categorie_${index + 1}`;
}

/** Écarte une collision de clé (deux calques que la normalisation rendrait identiques). */
function uniqueKey(key: string, used: Set<string>): string {
	let candidate = key;
	for (let suffix = 2; used.has(candidate); suffix++) candidate = `${key}_${suffix}`;
	used.add(candidate);

	return candidate;
}

/**
 * Clé d'aperçu d'une catégorie : le calque, et le rang de sa forme s'il en désigne une.
 *
 * Deux catégories du même calque doivent pouvoir être prévisualisées séparément — sans quoi
 * cliquer sur « Forme 12 » allume les dix-huit formes de sa couleur, ce qui ne dit toujours pas
 * laquelle on coche.
 */
export function previewKey(category: Pick<PlanCategory, 'baseLayer' | 'shapes'>): string {
	const shape = category.shapes?.[0];

	return undefined === shape ? category.baseLayer : `${category.baseLayer}#${shape}`;
}

/**
 * Coche ou décoche TOUTES les lignes.
 *
 * Le serveur propose une ligne par forme sur un plan raster : trente-sept cases à cocher une
 * à une, ce n'est pas un travail d'opérateur.
 */
export function toggleAll(categories: PlanCategory[], enabled: boolean): PlanCategory[] {
	return categories.map(category => ({ ...category, enabled }));
}

/**
 * Applique un type de POI à toutes les lignes COCHÉES.
 *
 * ⚠️ Les lignes décochées gardent le leur : décocher n'est pas renoncer au type qu'on avait
 * choisi, et l'opérateur qui recoche doit le retrouver intact.
 */
export function applyPlaceTypeToSelection(categories: PlanCategory[], placeTypeUrn: string): PlanCategory[] {
	return categories.map(category => (category.enabled ? { ...category, placeTypeUrn } : category));
}

/** Catégories réellement importables : cochées ET associées à un type de POI. */
export function readyCategories(categories: PlanCategory[]): PlanCategory[] {
	return categories.filter(category => category.enabled && !!category.placeTypeUrn);
}

/**
 * Corps de `POST /plan/mapping` construit depuis les catégories retenues.
 *
 * ⚠️ **`matchFieldMapping` n'est plus vide.** Il l'était, et le serveur retombait alors sur
 * `'label'` : sur un plan de salon, `label` porte le nom de l'exposant et le numéro de stand
 * vit dans `reference` ou dans un champ custom. Conséquence, jamais signalée : chaque réimport
 * d'un hall RECRÉAIT les POI en doublon au lieu de les mettre à jour. Une catégorie qui n'a
 * pas choisi reste absente du mapping — le serveur garde son défaut, et le laisser trancher
 * explicitement vaut mieux que de lui envoyer une chaîne vide qu'il devrait interpréter.
 */
export function toMappingPayload(categories: PlanCategory[]): {
	layerMapping: DxfSuggestedCategory[];
	categoryPlaceTypeMapping: Record<string, string>;
	matchFieldMapping: Record<string, string>;
} {
	const ready = readyCategories(categories);

	return {
		// ⚠️ `shapes` DOIT être transmis : c'est lui qui distingue deux catégories partageant
		// le même `baseLayer`. Omis, le serveur reversait les 37 formes du plan dans la
		// première catégorie et n'appliquait qu'un seul type de POI.
		//
		// `forcedShapes` en est le miroir : les rangs gardés depuis la file des doutes malgré
		// le rejet de la liste blanche de formes simples.
		layerMapping: ready.map(({ key, label, baseLayer, numberLayer, briefLayer, shapes, forcedShapes, numbers }) => ({
			key,
			label,
			baseLayer,
			numberLayer,
			briefLayer,
			shapes,
			forcedShapes,
			numbers,
		})),
		categoryPlaceTypeMapping: Object.fromEntries(ready.map(category => [category.key, category.placeTypeUrn])),
		matchFieldMapping: Object.fromEntries(
			ready.filter(category => !!category.matchField).map(category => [category.key, category.matchField]),
		),
	};
}
